import {
	getDisplayedId,
	getFullId,
	getFullItemId,
	isRepeater,
	getOuterItemId,
	getItemId,
	getRepeaterItemsFromProps,
} from '@wix/thunderbolt-commons'
import type { Component, RepeaterDataItem, Structure } from '@wix/thunderbolt-becky-types'
import type { IPropsStore } from '@wix/thunderbolt-symbols'
import type { RepeatersData, DisplayedIdToRepeatedAncestorId } from '../types'

const removeHash = (value: string) => value && value.replace('#', '')

const getItemsFromData = (comp: Component, repeatersData: RepeatersData) =>
	comp.dataQuery ? (repeatersData[removeHash(comp.dataQuery)] as RepeaterDataItem).items : []

const getRepeaterAncestor = (id: string, structure: Structure) => {
	let compId: string | null = structure[id].parent
	while (compId && !isRepeater(structure[compId].componentType)) {
		compId = structure[compId].parent
	}
	return compId
}

export const isComponentRepeaterDescendant = (
	id: string,
	structure: Structure,
	props: IPropsStore,
	repeatersData: RepeatersData,
	displayedOnlyToRepeaterAncestor: DisplayedIdToRepeatedAncestorId
) => {
	if (displayedOnlyToRepeaterAncestor[id]) {
		return true
	}
	// handle dynamic items (e.g repeater connected to a dbsm collection)
	const outerItemId = getOuterItemId(id)
	const templateId = getFullId(id)
	if (!structure[templateId]) {
		return false
	}
	const repeaterCompId = getRepeaterAncestor(templateId, structure)
	if (!repeaterCompId) {
		return false
	}
	// TODO: check if we can remove getItemsFromData
	const items =
		getRepeaterItemsFromProps(getDisplayedId(repeaterCompId, outerItemId), props.get) ||
		getItemsFromData(structure[repeaterCompId], repeatersData)
	return items.includes(getFullItemId(id))
}

export const inflateRepeater = (
	comp: Component,
	props: IPropsStore,
	repeatersData: RepeatersData,
	outerItemId?: string
) => {
	const displayedId = outerItemId ? getDisplayedId(comp.id, outerItemId) : comp.id
	// TODO: check if we can remove getItemsFromData
	const items = getRepeaterItemsFromProps(displayedId, props.get) || getItemsFromData(comp, repeatersData)
	const [repeaterItemTemplateId] = comp.components!
	return {
		...comp,
		components: items.map((itemId: string) => getDisplayedId(repeaterItemTemplateId, itemId)) as Array<string>,
	}
}

const isInStaticRepeater = (id: string, templateId: string, structure: Structure, repeatersData: RepeatersData) => {
	const itemId = getItemId(id)
	const repeaterCompId = getRepeaterAncestor(templateId, structure) || ''
	const repeaterAncestor = structure[repeaterCompId]
	return getItemsFromData(repeaterAncestor, repeatersData).includes(itemId)
}

export const inflateRepeaterItem = (
	id: string,
	structure: Structure,
	props: IPropsStore,
	repeatersData: RepeatersData
) => {
	const fullItemId = getFullItemId(id)
	const outerItemId = getOuterItemId(id)
	const templateId = getFullId(id)
	const templateComp = structure[templateId]

	const isStatic = isInStaticRepeater(id, templateId, structure, repeatersData)

	const components = isRepeater(templateComp.componentType)
		? inflateRepeater(templateComp, props, repeatersData, fullItemId).components
		: templateComp.components?.map((compIdWithoutScope) => getDisplayedId(compIdWithoutScope, fullItemId))
	// check if the comp is a direct child of the repeater container
	const parentComp = templateComp.parent ? structure[templateComp.parent] : null
	const isParentRepeater = parentComp && isRepeater(parentComp.componentType)
	return templateComp
		? {
				...templateComp,
				id,
				components,
				parent: isParentRepeater
					? outerItemId
						? getDisplayedId(templateComp.parent!, outerItemId)
						: templateComp.parent
					: getDisplayedId(templateComp.parent!, fullItemId),
				dataQuery:
					templateComp.dataQuery && isStatic
						? getDisplayedId(templateComp.dataQuery, fullItemId)
						: templateComp.dataQuery,
				designQuery:
					templateComp.designQuery && isStatic
						? getDisplayedId(templateComp.designQuery, fullItemId)
						: templateComp.designQuery,
		  }
		: structure[id]
}
